import type { MeSpouse } from 'api/models/response/MeResponse';
import { SELECTED_USER_STORAGE_KEY } from 'constant/selectedAccount';

export type AccountKind = 'self' | 'spouse';

export interface SwitchableAccount {
    firstname: string;
    id: number;
    kind: AccountKind;
    lastname: string;
}

export interface PersistedSelectedAccount {
    ownerUserId: number;
    selectedUserId: number;
}

export function isSpouseAccount(value: unknown): value is MeSpouse {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const id = (value as MeSpouse).id;
    return typeof id === 'number' && Number.isFinite(id) && id > 0;
}

export function isProfileOnlySpouseSelected(
    selfId: number,
    spouse: MeSpouse | null | undefined,
    selectedUserId: number | null
): boolean {
    return (
        isSpouseAccount(spouse) &&
        spouse.id !== selfId &&
        spouse.id === selectedUserId &&
        spouse.user_kind === 'PROFILE_ONLY'
    );
}

export function formatAccountName(firstname?: string | null, lastname?: string | null): string {
    return [firstname, lastname]
        .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
        .map((part) => part.trim())
        .join(' ');
}

export function getAccountInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) {
        return '?';
    }
    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

export function resolveSelectedUserId(
    selfId: number,
    spouse: MeSpouse | null,
    persistedSelectedId: number | null
): number {
    if (spouse && persistedSelectedId === spouse.id) {
        return spouse.id;
    }
    if (selfId && persistedSelectedId === selfId) {
        return selfId;
    }
    return selfId || 0;
}

function isPersistedSelectedAccount(value: unknown): value is PersistedSelectedAccount {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const record = value as PersistedSelectedAccount;
    return (
        typeof record.ownerUserId === 'number' &&
        Number.isFinite(record.ownerUserId) &&
        record.ownerUserId > 0 &&
        typeof record.selectedUserId === 'number' &&
        Number.isFinite(record.selectedUserId) &&
        record.selectedUserId > 0
    );
}

export function getPersistedSelectedAccount(): PersistedSelectedAccount | null {
    try {
        const raw = localStorage.getItem(SELECTED_USER_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed: unknown = JSON.parse(raw);
        return isPersistedSelectedAccount(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

export function getPersistedSelectedUserId(): number | null {
    return getPersistedSelectedAccount()?.selectedUserId ?? null;
}

/**
 * Spouse user id when the account switcher is on the partner.
 * Self stays on `/candidate_info/{Table}` (no id). Spouse uses `/{Table}/{id}`.
 */
export function getActingProfileUserId(): number | undefined {
    const persisted = getPersistedSelectedAccount();
    if (!persisted) {
        return undefined;
    }
    if (persisted.selectedUserId === persisted.ownerUserId) {
        return undefined;
    }
    return persisted.selectedUserId;
}

export function isActingAsSpouseProfile(): boolean {
    return getActingProfileUserId() !== undefined;
}

export function persistSelectedAccount(ownerUserId: number, selectedUserId: number): void {
    if (!ownerUserId || !selectedUserId) {
        return;
    }
    const payload: PersistedSelectedAccount = { ownerUserId, selectedUserId };
    localStorage.setItem(SELECTED_USER_STORAGE_KEY, JSON.stringify(payload));
}

export function clearPersistedSelectedAccount(): void {
    localStorage.removeItem(SELECTED_USER_STORAGE_KEY);
}
